import type { JSX, ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { getMessages } from "./messages";
import useLocale from "./use-locale";

type TextProps = {
  id: string;
  html?: boolean;
  tagName?: keyof JSX.IntrinsicElements;
  children?: ReactNode;
  [values: string]: unknown;
};

// Falls back to the raw message id (not another locale's text) when a key is missing, matching prior eo-locale behavior.
function defaultMessageFor(locale: string, id: string): string {
  return getMessages(locale)[id] ?? id;
}

export function Text({ id, html, tagName = "span", children: _children, ...values }: TextProps) {
  const locale = useLocale();
  const intl = useIntl();
  const messageValues = values as Record<string, string | number>;
  const defaultMessage = defaultMessageFor(locale, id);

  if (html) {
    const Tag = tagName;
    return (
      <Tag
        // biome-ignore lint/security/noDangerouslySetInnerHtml: trusted translation catalog, not user input
        dangerouslySetInnerHTML={{
          // ignoreTag: messages embed literal HTML (e.g. mailto links); without it intl-messageformat throws INVALID_TAG.
          __html: intl.formatMessage({ id, defaultMessage }, messageValues, { ignoreTag: true }),
        }}
      />
    );
  }

  return <FormattedMessage id={id} defaultMessage={defaultMessage} values={messageValues} />;
}

export function useTranslator() {
  const intl = useIntl();
  const locale = useLocale();
  return {
    translate: (id: string, values?: Record<string, unknown>) =>
      intl.formatMessage(
        { id, defaultMessage: defaultMessageFor(locale, id) },
        values as Record<string, string | number>,
      ),
  };
}
